// Central HTTP client for Convorcher's admin web. Sends `avataros_session`
// (cookie set by avatar-auth-service after portal SSO) automatically via
// `credentials: 'include'`. On 401 it throws UnauthorizedError; callers
// (UserContext) catch and redirect to the portal login.
//
// Local dev: set LOCAL_DEV_API_KEY=aos_... in .env to inject X-API-Key
// on every request instead of relying on the cross-domain SSO cookie. The
// key must be a real portal-minted aos_ key; Convorcher's Go server
// verifies it against avatar-auth-service (ENG-603).
//
// No refresh-retry path here — unlike avatar-customer-portal, this client
// never calls auth-service directly (portal owns all auth UX). The portal
// holds the refresh token and rotates the access token on its own pages.

import { ApiError, UnauthorizedError } from './errors'
import { localApiKey } from '../lib/viteEnv'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface FetchOptions {
  method?: HttpMethod
  body?: unknown
  query?: Record<string, string | number | undefined>
  signal?: AbortSignal
  headers?: Record<string, string>
}

function buildUrl(path: string, query?: FetchOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) params.set(k, String(v))
  }
  const qs = params.toString()
  if (!qs) return path
  return path + (path.includes('?') ? '&' : '?') + qs
}

function buildInit(opts: FetchOptions): RequestInit {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    // When LOCAL_DEV_API_KEY is set we omit credentials so a stale
    // avataros_session cookie (set by the portal on a sibling localhost
    // port) can't reach Convorcher and short-circuit the api_key path
    // with a JWKS-verification failure. In all other deployments the
    // cookie is the credential, so include it.
    credentials: localApiKey ? 'omit' : 'include',
  }
  if (opts.signal) init.signal = opts.signal
  const headers: Record<string, string> = { ...(opts.headers ?? {}) }
  if (localApiKey) headers['X-API-Key'] = localApiKey
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body)
  } else if (opts.body !== undefined) {
    init.body = opts.body as BodyInit
  }
  if (Object.keys(headers).length > 0) init.headers = headers
  return init
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const text = await res.text()
    if (!text) return `HTTP ${res.status}`
    try {
      const body = JSON.parse(text) as { error?: string }
      if (body && typeof body.error === 'string') return body.error
    } catch {
      // Plain text body — use it directly.
      return text
    }
  } catch {
    // Fall through
  }
  return `HTTP ${res.status}`
}

function elapsedMs(t0: number): number {
  return Math.round(performance.now() - t0)
}

export async function apiFetchRaw(path: string, opts: FetchOptions = {}): Promise<Response> {
  const url = buildUrl(path, opts.query)
  const init = buildInit(opts)
  const method = init.method ?? 'GET'

  const t0 = performance.now()
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    console.log(`[api] ${method} ${path} network-error (${elapsedMs(t0)}ms)`)
    throw err
  }
  console.log(`[api] ${method} ${path} ${res.status} (${elapsedMs(t0)}ms)`)

  if (res.status === 401) throw new UnauthorizedError()
  if (!res.ok) throw new ApiError(res.status, await readErrorMessage(res))
  return res
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOptions = {}): Promise<T> {
  const res = await apiFetchRaw(path, opts)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
